import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/gallery", label: "Gallery" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const solid = scrolled || pathname !== "/";

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        solid
          ? "bg-[#FDFBF7]/95 backdrop-blur-xl border-b border-[#E5E0D8]"
          : "bg-white/10 backdrop-blur-md border-b border-white/10"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_green-joinery/artifacts/7zg5z0zf_21E4688E-75F7-4055-B92D-0D3F251D117A.png"
            alt="Woodland Joinery and Building"
            className={`h-14 w-14 object-contain transition-all duration-500 ${
              solid ? "" : "drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]"
            }`}
          />
          <div className="hidden sm:flex flex-col leading-tight">
            <span
              className={`font-display text-lg tracking-wide ${
                solid ? "text-[#1F3B2D]" : "text-white"
              }`}
            >
              Woodland
            </span>
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#C8A24B]">
              Joinery &amp; Building
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm tracking-wide font-medium transition-colors ${
                  solid
                    ? isActive
                      ? "text-[#1F3B2D]"
                      : "text-[#4A5550] hover:text-[#1F3B2D]"
                    : isActive
                      ? "text-white"
                      : "text-white/80 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a
            href="tel:+447856925823"
            data-testid="nav-phone"
            className={`flex items-center gap-2 text-sm ${
              solid ? "text-[#1F3B2D]" : "text-white"
            }`}
          >
            <Phone className="h-4 w-4 text-[#C8A24B]" />
            <span className="tracking-wide">07856 925823</span>
          </a>
          <Link to="/contact" data-testid="nav-cta-quote" className="btn-primary">
            Free Quote
          </Link>
        </div>

        <button
          data-testid="nav-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className={`lg:hidden p-2 ${solid ? "text-[#1F3B2D]" : "text-white"}`}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#FDFBF7] border-t border-[#E5E0D8]"
        >
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-lg font-display ${
                    isActive ? "text-[#1F3B2D]" : "text-[#4A5550]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-cta-quote"
              className="btn-primary self-start mt-2"
            >
              Free Quote
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
